/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {computed, signal} from '@preact/signals-react';

/**
 * Whether the live job updates feature is enabled at all.
 *
 * Seeded once at bootstrap by whoever knows about `FeatureFlags`, the same way
 * `socketConnected` below is seeded by whoever knows about the socket - which
 * is what keeps this module free of both dependencies and testable without
 * either. It is never written again for the life of the page.
 *
 * The default is `false` so that a page which never seeds it is dark. The
 * feature reaches production behind `?features=livejobs`, and a caller which
 * forgets to ask gets the feature off rather than silently live.
 */
export const liveJobsEnabled = signal<boolean>(false);

export const setLiveJobsEnabled = (value: boolean): void => {
  liveJobsEnabled.value = value;
};

/**
 * The user's live-updates preference, driven by the sensor toggle in the job
 * grid's action bar. Deliberately untouched by the socket: a manual off
 * survives a drop and a reconnect, and only a drop-while-on returns to on by
 * itself.
 */
export const liveUpdatesPreferred = signal<boolean>(true);

export const setLiveUpdatesPreferred = (value: boolean): void => {
  liveUpdatesPreferred.value = value;
};

/**
 * Whether the WebSocket is currently connected. Mirrored from
 * `onSocketConnectionChange` in `initialization.ts`, which also seeds it from
 * `isSocketConnected()` - hence the `false` default, which is what
 * `EntityObserver.isConnected` reads before the first connection lands.
 */
export const socketConnected = signal<boolean>(false);

export const setSocketConnected = (value: boolean): void => {
  socketConnected.value = value;
};

/**
 * Whether the `jobProgress` channel - the grid and the open job tabs - should
 * be live. This is the sensor toggle's entire scope, and it is read at exactly
 * one choke point, `JobSubscriptions.sync()`.
 */
export const liveGridActive = computed<boolean>(() => (
  liveJobsEnabled.value && liveUpdatesPreferred.value && socketConnected.value
));

/**
 * Whether the `jobStatus` channel - the watch bells and the header
 * notification panel - should be live. The feature flag alone: a grid-local
 * control does not get to silence a global feature, so the sensor toggle
 * deliberately cannot reach this, and a user who turns live updates off still
 * gets the notifications for the jobs they are watching.
 *
 * It also does not read `socketConnected`, and must not:
 * `EntityObserver.syncSubscriptions` replays every observed id once the socket
 * opens, so subscribing while disconnected is already correct and gating on the
 * connection would only add a way to get it wrong.
 */
export const liveStatusActive = computed<boolean>(() => liveJobsEnabled.value);
